import { useMemo, useState, useEffect } from 'react';
import {
  Card,
  Grid,
  Avatar,
  Typography,
  Box,
  Chip,
  useMediaQuery,
  Button,
} from '@mui/material';
import DefaultCompany from './../../icons/building_round.svg';
import { useAccount } from '../../contexts/Account';
import eventBus, { eventBusValues } from '../../eventBus';
import AvatarsInvolved from '../../components/AvatarsInvolved';
import CampaignIcon from '@mui/icons-material/Campaign';

function CompanyContact({ contact }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        mr: '20px',
        mb: '10px',
      }}
    >
      <Box sx={{ width: '51px', height: '51px' }}>
        <Avatar
          src={`https://${contact.image.location}` || DefaultCompany}
          sx={{ width: '51px', height: '51px' }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', mx: '10px' }}>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            color: '#001030',
            lineHeight: '24px',
            whiteSpace: 'nowrap',
          }}
        >
          {contact.displayName}
        </Typography>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 500,
            color: '#001030',
            lineHeight: '22px',
          }}
        >
          {contact.title}
        </Typography>
      </Box>
    </Box>
  );
}

export default function BoardroomHeader({
  company,
  seeOpportunity,
  setIsFirstViewModalVisible,
}) {
  const { accountDetails, isFreeTrial, currentUserInfo } = useAccount();
  const companyIsP2P = company.boardType == "P2P"
  const isOwnProfile = company.id === accountDetails.id;
  const boardOwner = company?.contacts?.find(
    (contact) => contact.isBoardOwner === true
  );
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  const activeSortedBoardAdvisors = useMemo(() => {
    if (!company || !company.boardAdvisors) return [];
    return company?.boardAdvisors
      ?.filter(
        (advisor) =>
          advisor.advisorStatus === 'active' || advisor.advisorStatus === 'new'
      )
      .sort((a, b) => b.joinedOnDate.localeCompare(a.joinedOnDate))
      .map((bm) => {
        const advisor = bm.advisor || {};
        if (advisor.image && advisor.image.imageKey) {
          return `${process.env.REACT_APP_IMAGES_URL}${advisor.image.imageKey}`;
        }
        return null;
      });
  }, [company]);

  function openProfile(company) {
    eventBus.dispatch(eventBusValues.triggerCompanyDrawerAction, {
      company,
    });
  }

  return (
    <Card
      sx={{
        // mb: 3,
        p: 3,
        borderRadius: '8px',
        boxShadow: 'none',
        height: !isSM ? '100%' : 'auto',
      }}
    >
      <Grid container spacing={3} alignItems="center" justifyContent={'center'}>
        <Grid item>
          <Box
            id="boardroom-avatar"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: { xxs: 128, xs: 180 },
              height: { xxs: 128, xs: 140 },
              // border: '1px solid #DEE9FE',
              borderRadius: '8px',
            }}
          >
            <Avatar
              alt={`${company.companyName} Logo`}
              src={
                company &&
                company.image &&
                company.image.id &&
                !company.image.id.includes('undefined')
                  ? `${process.env.REACT_APP_IMAGES_URL}${company.image.id}`
                  : DefaultCompany
              }
              sx={{
                width: 140,
                height: 140,
                // borderRadius: '8px',
              }}
              onClick={() =>
                isOwnProfile
                  ? openProfile(accountDetails)
                  : seeOpportunity(company.id)
              }
            ></Avatar>
          </Box>
        </Grid>
        <Grid item xs>
          <Typography
            id="boardroom-company-name"
            sx={{
              fontWeight: '600',
              fontSize: { xxs: 24, xs: 32 },
              textAlign: { xxs: 'center', xs: 'left' },
              lineHeight: '41px',
              color: '#001030',
            }}
          >
            {company.companyName} { companyIsP2P ? "" : "Advisory Board"}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xxs: 'column', xs: 'row' },
              justifyContent: 'flex-start',
              mt: 2,
              gap: 2,
              maxWidth: '100%',
            }}
          >
            {boardOwner && <CompanyContact contact={boardOwner} />}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                gap: 2,
              }}
            >
              <AvatarsInvolved
                noText
                limit={5}
                avatars={activeSortedBoardAdvisors}
                avatarWidth={'41px'}
              />
              {activeSortedBoardAdvisors &&
                activeSortedBoardAdvisors.length > 5 && (
                  <Chip
                    label={`+ ${isXS ? 'more' : 'more advisors'}`}
                    sx={{
                      backgroundColor: '#E8EAED',
                      color: '#001030',
                      fontWeight: '500',
                      fontSize: 14,
                      lineHeight: '18px',
                      height: 32,
                      alignSelf: 'center',
                      mt: isXS ? '10px' : '-20px',
                    }}
                  />
                )}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'left',
              mt: 2,
            }}
          >
            <Button
              startIcon={<CampaignIcon sx={{ width: 24, height: 24 }} />}
              variant="contained"
              sx={{
                backgroundColor: '#2B71F6',
                color: '#FFFFFF',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: '20px',
                px: '24px',
                py: '6px',
              }}
              onClick={() => {
                setIsFirstViewModalVisible(true);
              }}
            >
              Get Involved
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
}
